/**
 * SpaceTypesPageV2 - Página de selección de tipos de espacios
 * Primera pantalla del flujo V2: muestra todos los tipos reservables
 */
import React, { useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTheme } from '../hooks/useTheme'
import { useTiposPublicos } from '../hooks/useTiposPublicos'
import { useEspacios } from '../hooks/useEspacios'
import { slugify } from '../utils/stringHelpers'
import { getImageUrl } from '../utils/imageUtils'
import SimpleHeader from '../components/common/SimpleHeader'
import LoadingSpinner from '../components/common/LoadingSpinner'
import ErrorMessage from '../components/common/ErrorMessage'
import Footer from '../components/common/Footer'

// Helper para obtener icono del tipo
const getTypeIcon = (nombre: string): string => {
  switch (nombre.toLowerCase()) {
    case 'cabaña':
    case 'cabana':
      return '🏠'
    case 'camping':
      return '⛺'
    case 'glamping':
      return '🏕️'
    case 'casa':
      return '🏡'
    case 'meson':
    case 'mesón':
      return '🍽️'
    default:
      return '🏕️'
  }
}

const SpaceTypesPageV2: React.FC = () => {
  const { colors } = useTheme()
  const navigate = useNavigate()
  const { data: tipos = [], isLoading: loadingTipos, error: errorTipos } = useTiposPublicos()
  const { data: espacios = [], isLoading: loadingEspacios, error: errorEspacios } = useEspacios()

  // Filtrar solo tipos reservables y públicos
  const tiposReservables = useMemo(() =>
    tipos.filter(tipo => tipo.reservable && !tipo.privado),
    [tipos]
  )

  // Contar espacios por tipo
  const tiposConConteo = useMemo(() =>
    tiposReservables.map(tipo => ({
      ...tipo,
      countEspacios: espacios.filter(e => e.tipo_nombre === tipo.nombre).length
    })),
    [tiposReservables, espacios]
  )

  const isLoading = loadingTipos || loadingEspacios
  const error = errorTipos || errorEspacios

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <LoadingSpinner />
      </div>
    )
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-6">
        <ErrorMessage message="Error al cargar los tipos de espacios" />
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <SimpleHeader
        title="Explora Nuestros Espacios"
        subtitle="Selecciona el tipo de espacio que prefieres y descubre todas las opciones disponibles"
      />

      {/* Grid de tipos */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        {tiposConConteo.length === 0 ? (
          <div className="text-center py-12">
            <div className="text-6xl mb-4">🏕️</div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              No hay tipos de espacios disponibles
            </h2>
            <p className="text-gray-600">
              Vuelve más tarde o contacta al administrador
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {tiposConConteo.map(tipo => (
              <button
                key={tipo.id}
                onClick={() => navigate(`/reservar/${slugify(tipo.nombre)}`)}
                className="tipo-card bg-white rounded-lg shadow-md hover:shadow-2xl transition-all duration-300 overflow-hidden group"
              >
                {/* Imagen de fondo o gradient */}
                <div className="h-48 sm:h-56 md:h-64 relative overflow-hidden group-hover:scale-105 transition-transform duration-300">
                  {tipo.imagen ? (
                    <>
                      {/* Imagen de fondo */}
                      <img
                        src={getImageUrl(tipo.imagen)}
                        alt={tipo.nombre}
                        className="absolute inset-0 w-full h-full object-cover"
                        onError={(e) => {
                          // Si falla la carga, ocultar imagen y mostrar fallback
                          const parent = e.currentTarget.parentElement
                          if (parent) {
                            parent.innerHTML = `
                              <div class="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-gray-100 to-gray-200">
                                <div class="text-8xl">${getTypeIcon(tipo.nombre)}</div>
                              </div>
                            `
                          }
                        }}
                      />
                      {/* Overlay oscuro para legibilidad */}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent" />

                      {/* Contenido sobre la imagen */}
                      <div className="absolute inset-0 p-4 sm:p-6 flex flex-col justify-between z-10">
                        <div>
                          <h3 className="text-xl sm:text-2xl font-bold text-white mb-2">{tipo.nombre}</h3>
                          <p className="text-white/90 text-sm line-clamp-2">
                            {tipo.descripcion || `Espacios tipo ${tipo.nombre}`}
                          </p>
                        </div>

                        <div className="flex items-center justify-between">
                          <div className="text-sm text-white/90">
                            <span className="font-semibold text-white text-lg">{tipo.countEspacios}</span> espacios disponibles
                          </div>
                          <div
                            className="text-sm font-semibold px-3 py-1 rounded-full bg-white/20 text-white backdrop-blur-sm"
                          >
                            Ver todos →
                          </div>
                        </div>
                      </div>
                    </>
                  ) : (
                    /* Fallback: gradient y emoji */
                    <>
                      <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-gray-100 to-gray-200">
                        <div className="text-6xl sm:text-7xl md:text-8xl">{getTypeIcon(tipo.nombre)}</div>
                      </div>

                      {/* Contenido sobre el gradient */}
                      <div className="absolute inset-0 p-4 sm:p-6 flex flex-col justify-between z-10">
                        <div>
                          <h3 className="text-2xl font-bold text-gray-900 mb-2">{tipo.nombre}</h3>
                          <p className="text-gray-600 text-sm line-clamp-2">
                            {tipo.descripcion || `Espacios tipo ${tipo.nombre}`}
                          </p>
                        </div>

                        <div className="flex items-center justify-between">
                          <div className="text-sm text-gray-500">
                            <span className="font-semibold text-gray-900 text-lg">{tipo.countEspacios}</span> espacios disponibles
                          </div>
                          <div
                            className="text-sm font-semibold px-3 py-1 rounded-full"
                            style={{
                              backgroundColor: `${colors.primary}20`,
                              color: colors.primary
                            }}
                          >
                            Ver todos →
                          </div>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      <Footer />
    </div>
  )
}

export default SpaceTypesPageV2
